import React, {useEffect, useState} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    CircularProgress,
    MenuItem,
    IconButton,
    Typography,
    Grid,
    Stack,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import {
    createDept,
    Dept,
    getDeptById,
    queryDeptMsg, updateDept
} from '@/services/manageService/deptService';
import {queryUsersByDeptIds} from '@/services/manageService/userService';
import type {UserInfo} from '@/store/admin/userInfo';
import {handleFormValidate, deptRequiredFields} from '@/utils/formValidateUtil';
import {usePageCrud} from '@/hooks/usePageCrud';
import {InputField, SnackbarField} from '@/components/common';

// ========== 统一 props，与 UserDetail 风格一致 =============
interface DeptDetailProps {
    open: boolean;
    deptId?: number | null;
    isAdd?: boolean;
    onClose: (data?: any) => void;
}

interface DeptForm {
    pid: number;
    name: string;
    remark: string;
    companyName: string;
    deptHeadId: number;
    deptHeadName: string;
    currentNum: number;
    num: number;
}

const initialForm: DeptForm = {
    pid: 0,
    name: '',
    remark: '',
    companyName: '',
    deptHeadId: 0,
    deptHeadName: '',
    currentNum: 0,
    num: 0
};

const DeptDetail: React.FC<DeptDetailProps> = ({open, deptId, onClose, isAdd}) => {
    const theme = useTheme();
    const {canCreate, canUpdate} = usePageCrud({
        read: 'admin_dept_detail',
        create: 'admin:dept:create',
        update: 'admin:dept:update',
        remove: 'admin:dept:delete',
    });
    const [dept, setDept] = useState<Dept | null>(null);
    const [form, setForm] = useState<DeptForm>(initialForm);
    const [formError, setFormError] = useState<{ companyName?: string; name?: string }>({});
    const [loading, setLoading] = useState(false);
    const [deptMsg, setDeptMsg] = useState<{ id: string; name: string }[]>([]);
    const [userOption, setUserOption] = useState<{ id: number; name: string }[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error'
    }>({open: false, message: '', severity: 'success'});
    // Superadmin dept: display-only (fixed id 1001 or name=superadmin)
    const isSuperadminReadOnly = !isAdd && (
        deptId === 1001
        || String(form.name ?? '').toLowerCase() === 'superadmin'
        || String(dept?.name ?? '').toLowerCase() === 'superadmin'
    );

    const handleClose = (refresh = false) => {
        onClose(refresh);
    };

    // -----------------------------------------------------
    // -------------- Component Initial --------------------
    // -----------------------------------------------------

    useEffect(() => {
        if (isAdd && open) {
            setLoading(true);
            setForm({...initialForm});
            setDept(null);
            setUserOption([]);
            setLoading(false)
        } else if (deptId && open) {
            setLoading(true);
            getDeptById(deptId).then(res => {
                let data = Array.isArray(res) ? res[0] : res;
                setDept(data);
                setForm({...initialForm, ...data});
                if (data && data.deptId) {
                    queryUsersByDeptIds(data.deptId).then((users: UserInfo[] = []) => {
                        const userArr = Array.isArray(users) ? users.map(u => ({
                            id: u.userId,
                            name: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || '-'
                        })) : [];
                        setUserOption(userArr);
                    });
                } else {
                    setUserOption([]);
                }
            }).finally(() => setLoading(false));
        }
    }, [deptId, open, isAdd]);

    // -----------------------------------------------------
    // -------------- Selection Initial --------------------
    // -----------------------------------------------------
    useEffect(() => {
        queryDeptMsg().then((res) => {
            setDeptMsg(Object.entries(res)
                .map(([id, name]) => ({
                    id,
                    name: String(name)
                })));
        });
    }, []);

    // -----------------------------------------------------
    // ----------------- Validate Handler ------------------
    // -----------------------------------------------------

    const onFormValidate = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
        const { name, value } = e.target as HTMLInputElement;
        setForm({ ...form, [name]: value });
        handleFormValidate(name, value, deptRequiredFields, setFormError);
    };

    const handleSelectChange = (field: string, value: any) => {
        setForm({...form, [field]: value});
    };

    const validate = () => {
        const errors: any = {};
        deptRequiredFields.forEach(field => {
            if (!form[field as keyof DeptForm] || (typeof form[field as keyof DeptForm] === 'string' && (form[field as keyof DeptForm] as string).trim() === '')) {
                errors[field] = 'required fields!';
            }
        });
        setFormError(errors);
        return Object.keys(errors).length === 0;
    };

    // -----------------------------------------------------
    // ----------------- Button Handler --------------------
    // -----------------------------------------------------
    const handleCreate = () => {
        if (!validate()) return;
        setSubmitting(true);
        const payload = {...form};
        createDept(payload)
            .then((res: any) => {
                setSubmitting(false);
                onClose({
                    type: 'create',
                    code: res.code,
                    message: res.name + ' Create successful!'
                })
            })
            .catch(e => {
                setSubmitting(false);
                setSnackbar({open: true, message: e?.message || 'Dept Create failed!', severity: 'error'});
            });
    };

    const handleSave = () => {
        if (!validate() || !deptId) return;
        setSubmitting(true);
        const payload = {...form};
        updateDept(deptId, payload)
            .then((res: any) => {
                setSubmitting(false);
                onClose({
                    type: 'update',
                    code: res.code,
                    message: res.name + ' Update successful!'
                });
            })
            .catch(e => {
                setSubmitting(false);
                setSnackbar({open: true, message: e?.message || 'Dept Update failed!', severity: 'error'});
            });
    };

    if ((isAdd && !open) || (!isAdd && !dept && !loading)) return null;

    return (
        <Dialog
            open={open}
            onClose={() => handleClose(false)}
            maxWidth="sm"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    bgcolor: theme.palette.background.paper,
                    boxShadow: theme.shadows[3]
                }
            }}
        >
            <DialogTitle sx={{m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                <Typography variant="inherit">{isAdd ? 'Add Dept' : 'Dept Detail'}</Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => handleClose(false)}
                    sx={{color: theme.palette.grey[500]}}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent
                dividers
                sx={{
                    bgcolor: theme.palette.background.default
                }}
            >
                {(loading || submitting) ? (
                    <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
                        <CircularProgress color="secondary"/>
                    </Box>
                ) : (
                    <Box component="form" noValidate autoComplete="off" sx={{pt: 1}}>
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <InputField
                                    label="Parent Dept"
                                    select
                                    fullWidth
                                    name="pid"
                                    value={form.pid === 0 ? '0' : form.pid?.toString() || ''}
                                    onChange={e => handleSelectChange('pid', Number(e.target.value))}
                                    display={isSuperadminReadOnly}
                                >
                                    <MenuItem value="0">root</MenuItem>
                                    {deptMsg.map((item) => (
                                        <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                                    ))}
                                </InputField>
                            </Grid>
                            <Grid size={12}>
                                <InputField
                                    label="Company Name"
                                    name="companyName"
                                    value={form.companyName}
                                    fullWidth
                                    onChange={onFormValidate}
                                    required
                                    error={!!formError.companyName}
                                    helperText={formError.companyName || ''}
                                    display={isSuperadminReadOnly}
                                />
                            </Grid>
                            <Grid size={12}>
                                <InputField
                                    label="Dept Name"
                                    name="name"
                                    value={form.name}
                                    fullWidth
                                    onChange={onFormValidate}
                                    required
                                    error={!!formError.name}
                                    helperText={formError.name || ''}
                                    display={isSuperadminReadOnly}
                                />
                            </Grid>
                            <Grid size={12}>
                                <InputField
                                    label="Remark"
                                    name="remark"
                                    value={form.remark}
                                    fullWidth
                                    onChange={onFormValidate}
                                    display={isSuperadminReadOnly}
                                />
                            </Grid>
                            <Grid size={12}>
                                <InputField
                                    label="Dept Head Name"
                                    select
                                    fullWidth
                                    name="deptHeadId"
                                    value={form.deptHeadId?.toString() || ''}
                                    onChange={e => handleSelectChange('deptHeadId', e.target.value)}
                                    display={isSuperadminReadOnly}
                                >
                                    <MenuItem value="">None</MenuItem>
                                    {userOption.map((u) => (
                                        <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                                    ))}
                                </InputField>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 2, py: 1.5 }}>
                <Stack direction="row" spacing={1} sx={{px: 1, py: 0.5}}>
                    <Button variant="outlined" onClick={() => handleClose(false)}
                            disabled={submitting || loading}
                            sx={{textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius}}>Cancel</Button>
                    {isAdd ? (
                        canCreate && (
                            <Button variant="contained" color="secondary" onClick={handleCreate}
                                    disabled={submitting || loading}
                                    sx={{textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius}}>Create</Button>
                        )
                    ) : (
                        !isSuperadminReadOnly && canUpdate && (
                            <Button onClick={handleSave} variant="contained" color="primary"
                                    sx={{textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius}}
                                    disabled={submitting}>
                                {submitting ? <CircularProgress size={20} color="inherit"/> : 'Save'}
                            </Button>
                        )
                    )}
                </Stack>
            </DialogActions>
            <SnackbarField
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                autoHideDuration={4000}
                onClose={() => setSnackbar({...snackbar, open: false})}
            />
        </Dialog>
    );
};

export default DeptDetail;
